import { Signal } from '@angular/core';

/**
 * What a root-schema load is doing. A union rather than a `{ status, data }` bag: the schema is
 * unreachable unless it actually arrived, so no consumer can read a value that is not there.
 */
export type SchemaLoadState<TSchema> =
  | {
      /**
       * Criteria are incomplete, so nothing was requested. **`idle` counts as loading** for every
       * consumer — `isSchemaLoading` below is the one place that rule is written down, and a shell
       * that wants to tell "not started" apart from "in flight" changes it there rather than in its
       * own derivation. Deciding it per store is how the three shells drifted apart before.
       */
      kind: 'idle';
    }
  | { kind: 'loading' }
  | { kind: 'reloading'; schema: TSchema }
  | { kind: 'loaded'; schema: TSchema }
  | { kind: 'error'; error: string };

export interface SchemaLoader<TSchema> {
  readonly state: Signal<SchemaLoadState<TSchema>>;
  /**
   * `state` put through the three helpers below, applied once here instead of once per consumer.
   * Rendering goes through `SchemaRootOutletComponent` and takes `state`; these are for the readers
   * that want the schema without rendering it — a resolver reading `componentType`, a data source
   * keyed on the loaded document.
   */
  readonly schema: Signal<TSchema | null>;
  readonly isLoading: Signal<boolean>;
  readonly error: Signal<string | null>;
  /**
   * Re-runs the current request. **Silently does nothing when `schemaLoader`'s `cacheKey` is set and
   * the current criteria are already cached**: a cache hit issues no request at all, so there is
   * nothing in flight to re-run and the state does not change. Refreshing a cached schema needs cache
   * invalidation, which this loader deliberately does not have.
   */
  reload(): void;
}

/** The schema a consumer may render: present while `loaded` and while `reloading`, `null` otherwise. */
export function schemaOf<TSchema>(state: SchemaLoadState<TSchema>): TSchema | null {
  return state.kind === 'loaded' || state.kind === 'reloading' ? state.schema : null;
}

/** Includes `idle` — see the `idle` member of `SchemaLoadState` for why that is the shared rule. */
export function isSchemaLoading(state: SchemaLoadState<unknown>): boolean {
  return state.kind === 'idle' || state.kind === 'loading' || state.kind === 'reloading';
}

export function schemaErrorOf(state: SchemaLoadState<unknown>): string | null {
  return state.kind === 'error' ? state.error : null;
}
